import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOrganizationAssistants } from "../features/organization/organizationSlice";
import { fetchSettingDetailsByUserId } from "../features/settings/settingsSlice";
import {
  AVAILABLE_PLATFORMS,
  formatPlatformName,
  getIcon,
} from "../constants/constants";
import { Icons } from "../constants/icon-library";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "../components/common/LoadingComponent";
import { checkIntegrationSetup } from "./AssistantDetails/Assistants.utils";

function Assistants() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useSelector((state) => state.user);
  const { organizations, loading: orgLoading } = useSelector(
    (state) => state.organization,
  );
  const { userSettings, loading: settingLoading } = useSelector(
    (state) => state.setting,
  );
  const installedSettings = userSettings || [];
  const availableAssistants = Array.isArray(organizations) ? organizations : [];

  useEffect(() => {
    const makeRequest = () => {
      dispatch(
        fetchSettingDetailsByUserId({
          search: searchTerm,
          accountId: user.org,
        }),
      );
      if (user?.role) {
        dispatch(getOrganizationAssistants({ search: searchTerm }));
      }
    };

    let timeout;
    if (searchTerm.trim()) {
      timeout = setTimeout(makeRequest, 1000);
    } else {
      makeRequest();
    }

    return () => clearTimeout(timeout);
  }, [searchTerm, user, dispatch]);

  const loading =
    (!installedSettings.length && settingLoading) ||
    (!availableAssistants.length && orgLoading);

  const handleInstall = (assistant) => {
    navigate(`/apps/install/${assistant.uuid}`);
  };

  const handleConfiguration = (assistant, settingUuid) => {
    navigate(`/apps/installed/${assistant.uuid}/setting/${settingUuid}`);
  };

  return (
    <div className="container my-11">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Apps</h2>

        <div className="w-50 position-relative">
          <input
            type="text"
            className="form-control ps-5"
            placeholder="Search apps..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <i
            className={`wb ${Icons(
              "search",
            )} fs-8 text-secondary position-absolute`}
            style={{
              top: "50%",
              left: "15px",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          ></i>
        </div>
      </div>

      {loading ? (
        <LoadingComponent loading={true} size={15} />
      ) : (
        <>
          <div className="mb-5">
            <h4 className="mb-4 text-muted">INSTALLED</h4>

            {installedSettings.length === 0 ? (
              <div className="d-flex flex-column align-items-center">
                <p className="text-center mb-3">No active apps found.</p>
              </div>
            ) : (
              <div className="row g-4">
                {installedSettings.map((setting) => {
                  const assistant = setting?.assistantId;

                  if (!assistant) return null;
                  const platformName = assistant?.platform?.type?.toLowerCase();
                  let iconSrc = getIcon(platformName);

                  if (!iconSrc) {
                    const matchedAssistant = availableAssistants.find(
                      (a) => a?.platform?.type?.toLowerCase() === platformName,
                    );

                    iconSrc = matchedAssistant?.platform?.iconUrl;
                  }
                  const displayName = setting.assistantName || assistant.name;
                  const needsSetup = checkIntegrationSetup(
                    platformName,
                    setting,
                  );

                  return (
                    <div key={setting.uuid} className="col-md-3 col-lg-3">
                      <div className="card text-center shadow-sm h-100 position-relative">
                        {needsSetup ? (
                          <span
                            className="position-absolute badge text-setup"
                            style={{
                              top: "10px",
                              left: "10px",
                              fontSize: "0.7rem",
                              zIndex: 1,
                              border: "1px solid #f27b7b",
                              background: "transparent",
                            }}
                          >
                            Setup Required
                          </span>
                        ) : (
                          <span
                            className="position-absolute badge text-success"
                            style={{
                              top: "10px",
                              left: "10px",
                              fontSize: "0.7rem",
                              zIndex: 1,
                              border: "1px solid #38ef7d",
                              background: "transparent",
                            }}
                          >
                            Installed
                          </span>
                        )}

                        <button
                          className="btn btn-sm position-absolute"
                          onClick={() =>
                            handleConfiguration(assistant, setting.uuid)
                          }
                          style={{
                            color:
                              "var(--phoenix-navbar-vertical-link-active-color)",
                            width: "5px",
                            height: "5px",
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "2px",
                            zIndex: 1,
                            top: "25px",
                            right: "20px",
                          }}
                          title="Configuration"
                        >
                          <i className={`wb ${Icons("constraints")} fs-7`}></i>
                        </button>

                        <div className="card-body d-flex flex-column align-items-center pt-5">
                          <div
                            className="rounded-circle bg-secondary d-flex justify-content-center align-items-center mb-3"
                            style={{ width: "90px", height: "90px" }}
                          >
                            {iconSrc ? (
                              <img
                                src={iconSrc}
                                alt={assistant?.name}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <i className={`wb ${Icons("apps")} fs-3`} />
                            )}
                          </div>
                          <h5 className="card-title mb-3 text-truncate">
                            {displayName}
                          </h5>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <hr className="my-5" />
          <div>
            <h4 className="mb-4 text-muted">AVAILABLE</h4>

            {availableAssistants.length === 0 ? (
              <p className="text-center">No assistants available.</p>
            ) : (
              <div className="row g-4">
                {availableAssistants.map((assistant) => {
                  const platformName = assistant.platform?.type?.toLowerCase();
                  let iconSrc = getIcon(platformName);
                  if (iconSrc === undefined) {
                    iconSrc = assistant?.platform?.iconUrl;
                  }
                  const isAvailable =
                    AVAILABLE_PLATFORMS.includes(platformName);
                  return (
                    <div key={assistant.uuid} className="col-md-3 col-sm-6">
                      <div className="card text-center shadow-sm h-100 position-relative">
                        <div className="card-body d-flex flex-column align-items-center">
                          <div
                            className="rounded-circle bg-secondary d-flex justify-content-center align-items-center mb-3"
                            style={{
                              width: "90px",
                              height: "90px",
                            }}
                          >
                            {iconSrc ? (
                              <img
                                src={iconSrc}
                                alt={assistant?.name}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <i className={`wb ${Icons("apps")} fs-3`} />
                            )}
                          </div>

                          <h5 className="card-title mb-3">{assistant.name}</h5>

                          {isAvailable ? (
                            <button
                              className="btn btn-primary flex-grow-1"
                              onClick={() => handleInstall(assistant)}
                            >
                              <i
                                className={`wb ${Icons(
                                  "install",
                                )} fs-8 me-1 align-middle`}
                              ></i>{" "}
                              Install
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary flex-grow-1"
                              onClick={() =>
                                navigate("/apps/coming-soon", {
                                  state: {
                                    platformName: formatPlatformName(
                                      assistant?.platform?.type,
                                    ),
                                    assistant,
                                  },
                                })
                              }
                            >
                              <i
                                className={`wb ${Icons(
                                  "clock",
                                )} fs-8 me-1 align-middle`}
                              ></i>{" "}
                              Coming Soon
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Assistants;
